/* One extracted table, exactly as it was read out of the PDF.
 *
 * Shown wherever a reviewer has to judge a figure against the document: inside
 * the inline reconciliation, where the figures can be corrected, and beside a
 * finding, where the figures it rests on are outlined and nothing is editable.
 *
 * A 15-column table is wider than the pane. It scrolls inside its own frame
 * rather than widening the page, and the frame says so — an edge mark plus a
 * line of text — because a table that silently hides five columns is worse than
 * one that admits it. That frame is the shared ScrollFrame primitive, which
 * every wide block in the application now uses; what stays here is the table
 * itself and the sticky row-number column that makes it readable. */

import { ScrollFrame } from '../primitives';
import { isFigureCell, readTable } from '../../engine/deterministic/reconciliation';
import type { ExtractedTable } from '../../types';
import './TableView.css';

export interface TableViewProps {
  table: ExtractedTable;
  /** "row,col" -> the figure the reviewer typed in place of the parsed one. */
  corrections: Record<string, string>;
  onCorrect(key: string, value: string): void;
  /** "row,col" keys to outline: the figures a finding rests on. */
  highlight?: Set<string>;
  editable?: boolean;
}

export default function TableView({
  table,
  corrections,
  onCorrect,
  highlight,
  editable = true,
}: TableViewProps) {
  const shape = readTable(table);
  const all = [table.headers, ...table.rows];
  const headerCount = shape.headerRows.length;

  return (
    <ScrollFrame
      className="tv"
      frameClassName="tv__frame"
      label={`${table.label}${table.caption ? `, ${table.caption}` : ''} — ${shape.columnLabels.length} columns, scrolls sideways`}
      hint={`${shape.columnLabels.length} columns — wider than the pane. Scroll the table sideways, or focus it and use the arrow keys, to see the rest.`}
    >
      <table className="tv__table">
        <caption className="tv__caption">
          <span className="tv__label">{table.label}</span>
          <span>{table.caption}</span>
          <span className="tv__page">page {table.page}</span>
        </caption>
        <tbody>
          {all.map((cells, rowIndex) => {
            const isHeader = rowIndex < headerCount;
            return (
              <tr key={rowIndex} className={isHeader ? 'tv__row--header' : undefined}>
                <th scope="row" className="tv__row-num">
                  {rowIndex}
                </th>
                {Array.from({ length: shape.columnLabels.length }, (_, col) => {
                  const key = `${rowIndex},${col}`;
                  const raw = cells[col] ?? '';
                  const override = corrections[key];
                  const value = override ?? raw;
                  const figure = isFigureCell(value);
                  const classes = [
                    'tv__cell',
                    figure ? 'tv__cell--figure' : null,
                    override !== undefined ? 'tv__cell--corrected' : null,
                    highlight?.has(key) ? 'tv__cell--flagged' : null,
                  ]
                    .filter(Boolean)
                    .join(' ');

                  return (
                    <td key={col} className={classes}>
                      {editable && !isHeader && figure ? (
                        <input
                          className="tv__cell-input"
                          value={value}
                          aria-label={`${table.label} row ${rowIndex} column ${col}`}
                          onChange={(e) => onCorrect(key, e.target.value)}
                        />
                      ) : (
                        value
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </ScrollFrame>
  );
}
